import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  addServiceOrderAdminMessage,
  createServiceOrderAdjustmentAdmin,
  getServiceOrderAdmin,
  listServiceOrdersAdmin,
  updateServiceOrderAdmin,
} from "../../actions/serviceActions";
import {
  ADMIN_SERVICE_ORDER_ADJUSTMENT_RESET,
  ADMIN_SERVICE_ORDER_MESSAGE_RESET,
  ADMIN_SERVICE_ORDER_UPDATE_RESET,
} from "../../constants/serviceConstants";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import "./AdminServiceOrdersScreen.css";

const STATUS_OPTIONS = [
  "all",
  "pending_payment",
  "payment_processing",
  "payment_failed",
  "paid",
  "received",
  "in_progress",
  "awaiting_customer",
  "ready_for_collection",
  "ready_to_ship",
  "completed",
  "cancelled",
  "refunded",
];

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatStatus(status = "") {
  return safeText(status).replace(/_/g, " ") || "pending";
}

function formatDate(value = "") {
  if (!value) return "-";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "-";
  }
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function statusVariant(status = "") {
  switch (status) {
    case "paid":
    case "received":
    case "in_progress":
      return "success";
    case "completed":
      return "dark";
    case "payment_failed":
    case "cancelled":
      return "danger";
    case "refunded":
      return "secondary";
    default:
      return "warning";
  }
}

function OrderDetail({ serviceOrder }) {
  const dispatch = useDispatch();

  const updateState = useSelector((state) => state.adminServiceOrderUpdate || {});
  const messageState = useSelector((state) => state.adminServiceOrderMessage || {});
  const adjustmentState = useSelector(
    (state) => state.adminServiceOrderAdjustment || {},
  );

  const [status, setStatus] = useState(serviceOrder.status || "paid");
  const [adminNotes, setAdminNotes] = useState(serviceOrder.adminNotes || "");
  const [inboundTrackingNumber, setInboundTrackingNumber] = useState(
    serviceOrder.logistics?.inboundTrackingNumber || "",
  );
  const [inboundCarrier, setInboundCarrier] = useState(
    serviceOrder.logistics?.inboundCarrier || "",
  );
  const [outboundTrackingNumber, setOutboundTrackingNumber] = useState(
    serviceOrder.logistics?.outboundTrackingNumber || "",
  );
  const [outboundTrackingUrl, setOutboundTrackingUrl] = useState(
    serviceOrder.logistics?.outboundTrackingUrl || "",
  );
  const [outboundCarrier, setOutboundCarrier] = useState(
    serviceOrder.logistics?.outboundCarrier || "",
  );
  const [logisticsNotes, setLogisticsNotes] = useState(
    serviceOrder.logistics?.notes || "",
  );
  const [message, setMessage] = useState("");
  const [internalMessage, setInternalMessage] = useState(false);
  const [adjustmentLabel, setAdjustmentLabel] = useState("");
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");

  useEffect(() => {
    setStatus(serviceOrder.status || "paid");
    setAdminNotes(serviceOrder.adminNotes || "");
    setInboundTrackingNumber(serviceOrder.logistics?.inboundTrackingNumber || "");
    setInboundCarrier(serviceOrder.logistics?.inboundCarrier || "");
    setOutboundTrackingNumber(serviceOrder.logistics?.outboundTrackingNumber || "");
    setOutboundTrackingUrl(serviceOrder.logistics?.outboundTrackingUrl || "");
    setOutboundCarrier(serviceOrder.logistics?.outboundCarrier || "");
    setLogisticsNotes(serviceOrder.logistics?.notes || "");
  }, [serviceOrder]);

  useEffect(() => {
    if (updateState.success) {
      dispatch({ type: ADMIN_SERVICE_ORDER_UPDATE_RESET });
    }
    if (messageState.success) {
      dispatch({ type: ADMIN_SERVICE_ORDER_MESSAGE_RESET });
      setMessage("");
      setInternalMessage(false);
    }
    if (adjustmentState.success) {
      dispatch({ type: ADMIN_SERVICE_ORDER_ADJUSTMENT_RESET });
      setAdjustmentLabel("");
      setAdjustmentAmount("");
      setAdjustmentReason("");
    }
  }, [
    adjustmentState.success,
    dispatch,
    messageState.success,
    updateState.success,
  ]);

  const busy =
    updateState.loading || messageState.loading || adjustmentState.loading;

  const submitUpdate = (event) => {
    event.preventDefault();
    dispatch(
      updateServiceOrderAdmin(serviceOrder.reference, {
        status,
        adminNotes,
        inboundTrackingNumber,
        inboundCarrier,
        outboundTrackingNumber,
        outboundTrackingUrl,
        outboundCarrier,
        logisticsNotes,
      }),
    );
  };

  const submitMessage = (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    dispatch(
      addServiceOrderAdminMessage(serviceOrder.reference, message, {
        isInternal: internalMessage,
      }),
    );
  };

  const submitAdjustment = (event) => {
    event.preventDefault();
    if (!adjustmentLabel.trim()) return;
    dispatch(
      createServiceOrderAdjustmentAdmin(serviceOrder.reference, {
        label: adjustmentLabel,
        amount: Number(adjustmentAmount || 0),
        reason: adjustmentReason,
      }),
    );
  };

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={8}>
          <h1>{serviceOrder.reference}</h1>
          <div className="text-muted">
            {serviceOrder.serviceSnapshot?.name} |{" "}
            {serviceOrder.serviceSnapshot?.optionLabel}
          </div>
        </Col>
        <Col lg="auto">
          <Badge bg={statusVariant(serviceOrder.status)}>
            {formatStatus(serviceOrder.status)}
          </Badge>
        </Col>
      </Row>

      {updateState.error && <Message variant="danger">{updateState.error}</Message>}
      {messageState.error && <Message variant="danger">{messageState.error}</Message>}
      {adjustmentState.error && (
        <Message variant="danger">{adjustmentState.error}</Message>
      )}

      <Row className="g-4">
        <Col lg={7}>
          <Form onSubmit={submitUpdate}>
            <div className="admin-panel">
              <h2>Workflow</h2>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                    >
                      {STATUS_OPTIONS.filter((option) => option !== "all").map(
                        (option) => (
                          <option key={option} value={option}>
                            {formatStatus(option)}
                          </option>
                        ),
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Linked Order</Form.Label>
                    <Form.Control
                      value={serviceOrder.linkedOrderNumber || ""}
                      readOnly
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Inbound Carrier</Form.Label>
                    <Form.Control
                      value={inboundCarrier}
                      onChange={(event) => setInboundCarrier(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Inbound Tracking</Form.Label>
                    <Form.Control
                      value={inboundTrackingNumber}
                      onChange={(event) =>
                        setInboundTrackingNumber(event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Return Carrier</Form.Label>
                    <Form.Control
                      value={outboundCarrier}
                      onChange={(event) => setOutboundCarrier(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Return Tracking</Form.Label>
                    <Form.Control
                      value={outboundTrackingNumber}
                      onChange={(event) =>
                        setOutboundTrackingNumber(event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Tracking URL</Form.Label>
                    <Form.Control
                      value={outboundTrackingUrl}
                      onChange={(event) =>
                        setOutboundTrackingUrl(event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Logistics Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={logisticsNotes}
                      onChange={(event) => setLogisticsNotes(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Internal Admin Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={adminNotes}
                      onChange={(event) => setAdminNotes(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button type="submit" variant="dark" className="mt-3" disabled={busy}>
                Save updates
              </Button>
            </div>
          </Form>
        </Col>

        <Col lg={5}>
          <div className="admin-panel">
            <h2>Customer</h2>
            <p className="mb-1">
              <strong>{serviceOrder.customer?.name}</strong>
            </p>
            <p className="mb-1">
              <a href={`mailto:${serviceOrder.customer?.email}`}>
                {serviceOrder.customer?.email}
              </a>
            </p>
            <p className="mb-3">
              {serviceOrder.customer?.phone ? (
                <a href={`tel:${serviceOrder.customer.phone}`}>
                  {serviceOrder.customer.phone}
                </a>
              ) : (
                "No phone number"
              )}
            </p>

            <dl className="admin-facts">
              <div>
                <dt>Total</dt>
                <dd>
                  {formatMoney(
                    serviceOrder.pricing?.grandTotal,
                    serviceOrder.pricing?.currency,
                  )}
                </dd>
              </div>
              <div>
                <dt>Payment</dt>
                <dd>{formatStatus(serviceOrder.payment?.status)}</dd>
              </div>
              <div>
                <dt>Inbound</dt>
                <dd>{formatStatus(serviceOrder.logistics?.inboundMethod)}</dd>
              </div>
              <div>
                <dt>Return</dt>
                <dd>{formatStatus(serviceOrder.logistics?.outboundMethod)}</dd>
              </div>
            </dl>
          </div>

          <Form onSubmit={submitMessage} className="admin-panel mt-3">
            <h2>Message</h2>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={3}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
            </Form.Group>
            <Form.Check
              className="mt-2"
              type="checkbox"
              label="Internal note"
              checked={internalMessage}
              onChange={(event) => setInternalMessage(event.target.checked)}
            />
            <Button
              type="submit"
              variant="outline-dark"
              className="mt-3"
              disabled={busy || !message.trim()}
            >
              Add message
            </Button>
          </Form>

          <Form onSubmit={submitAdjustment} className="admin-panel mt-3">
            <h2>Adjustment</h2>
            <Form.Group className="mb-2">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={adjustmentLabel}
                onChange={(event) => setAdjustmentLabel(event.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={adjustmentAmount}
                onChange={(event) => setAdjustmentAmount(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Reason</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={adjustmentReason}
                onChange={(event) => setAdjustmentReason(event.target.value)}
              />
            </Form.Group>
            <Button
              type="submit"
              variant="outline-dark"
              className="mt-3"
              disabled={busy || !adjustmentLabel.trim()}
            >
              Request adjustment
            </Button>
          </Form>
        </Col>
      </Row>

      <Row className="g-4 mt-1">
        <Col lg={6}>
          <div className="admin-panel">
            <h2>Messages</h2>
            {(serviceOrder.messages || []).map((item) => (
              <div key={item.id} className="admin-note">
                <strong>
                  {formatStatus(item.authorType)}
                  {item.isInternal ? " internal" : ""}
                </strong>
                <p>{item.message}</p>
                <span>{formatDate(item.createdAt)}</span>
              </div>
            ))}
          </div>
        </Col>
        <Col lg={6}>
          <div className="admin-panel">
            <h2>Timeline</h2>
            {(serviceOrder.timeline || []).map((item) => (
              <div key={item.id || `${item.type}-${item.createdAt}`} className="admin-note">
                <strong>{item.label || formatStatus(item.type)}</strong>
                <p>{item.message}</p>
                <span>{formatDate(item.createdAt)}</span>
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </>
  );
}

export default function AdminServiceOrdersScreen() {
  const { reference = "" } = useParams();
  const dispatch = useDispatch();
  const [status, setStatus] = useState("all");

  const listState = useSelector((state) => state.adminServiceOrderList || {});
  const detailState = useSelector(
    (state) => state.adminServiceOrderDetails || {},
  );
  const updateState = useSelector((state) => state.adminServiceOrderUpdate || {});

  useEffect(() => {
    if (reference) {
      dispatch(getServiceOrderAdmin(reference));
      return;
    }

    dispatch(listServiceOrdersAdmin(status));
  }, [dispatch, reference, status, updateState.success]);

  const serviceOrders = useMemo(
    () => listState.serviceOrders || [],
    [listState.serviceOrders],
  );

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>{reference ? "Service Order" : "Service Orders"}</h1>
          <div className="text-muted">
            Track paid service work, logistics, customer messages and updates.
          </div>
        </Col>
        <Col md="auto" className="d-flex gap-2 flex-wrap align-items-center">
          {!reference ? (
            <Form.Select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Filter service orders by status"
              style={{ minWidth: 210 }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "All statuses" : formatStatus(option)}
                </option>
              ))}
            </Form.Select>
          ) : null}
          <Link to="/admin/services" className="btn btn-outline-dark">
            Services
          </Link>
          {reference ? (
            <Link to="/admin/services/orders" className="btn btn-dark">
              All service orders
            </Link>
          ) : null}
        </Col>
      </Row>

      {reference ? (
        <>
          {detailState.loading && <Loader />}
          {detailState.error && (
            <Message variant="danger">{detailState.error}</Message>
          )}
          {detailState.serviceOrder ? (
            <OrderDetail serviceOrder={detailState.serviceOrder} />
          ) : null}
        </>
      ) : (
        <>
          {listState.error && <Message variant="danger">{listState.error}</Message>}
          {listState.loading && <Loader />}
          <Table striped bordered hover responsive className="align-middle">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Customer</th>
                <th>Service</th>
                <th>Total</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {serviceOrders.map((order) => (
                <tr key={order.id || order.reference}>
                  <td>
                    <Link to={`/admin/services/orders/${order.reference}`}>
                      {order.reference}
                    </Link>
                  </td>
                  <td>
                    <div className="fw-semibold">{order.customer?.name}</div>
                    <div className="text-muted small">{order.customer?.email}</div>
                  </td>
                  <td>
                    <div className="fw-semibold">
                      {order.serviceSnapshot?.name}
                    </div>
                    <div className="text-muted small">
                      {order.serviceSnapshot?.quantity}{" "}
                      {order.serviceSnapshot?.unitLabel}
                    </div>
                  </td>
                  <td>
                    {formatMoney(
                      order.pricing?.grandTotal,
                      order.pricing?.currency,
                    )}
                  </td>
                  <td>
                    <Badge bg={statusVariant(order.status)}>
                      {formatStatus(order.status)}
                    </Badge>
                  </td>
                  <td>{formatStatus(order.payment?.status)}</td>
                  <td>{formatDate(order.updatedAt)}</td>
                </tr>
              ))}

              {serviceOrders.length === 0 && !listState.loading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No service orders found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
}
